import { useState, useEffect } from "react";
import "./createInvoice.css";
import dataService from "../services/dataService";
import { useNavigate } from "react-router-dom";
import Modal from "../components/Modal";

// Issue #9: simple product input with datalist autocomplete
function ItemRow({ item, index, products, taxTreatment, onChange, onRemove, showRemove }) {
  const handleField = (e) => onChange(index, e);

  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", padding: "10px", marginBottom: "10px", background: "#fafafa" }}>
      <div className="v-name" style={{ marginBottom: "6px", alignItems: "flex-end" }}>
        <div style={{ flex: 2 }}>
          <p>Item Name</p>
          {/* Issue #9: free-text input with datalist so user can type manually OR pick from catalog */}
          <input
            type="text"
            name="product"
            value={item.product}
            list={`prod-list-${index}`}
            onChange={handleField}
            placeholder="Type item name or search catalog..."
            style={{ width: "100%" }}
          />
          <datalist id={`prod-list-${index}`}>
            {products.map((p, i) => <option key={i} value={p.name} />)}
          </datalist>
        </div>
        <div style={{ flex: 2 }}>
          <p>Description (optional)</p>
          <input
            type="text"
            name="description"
            value={item.description || ""}
            onChange={handleField}
            placeholder="Custom details / specification..."
            style={{ width: "100%" }}
          />
        </div>
        {showRemove && (
          <button type="button" onClick={() => onRemove(index)} style={{ padding: "8px 10px", background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: "4px", cursor: "pointer", height: "36px" }}>×</button>
        )}
      </div>
      <div className="v-name" style={{ alignItems: "flex-end" }}>
        <div style={{ flex: 1 }}>
          <p>Qty</p>
          <input type="number" name="quantity" value={item.quantity} onChange={handleField} required min="0" />
        </div>
        <div style={{ flex: 1 }}>
          <p>Unit Price</p>
          <input type="number" name="price" value={item.price} onChange={handleField} required min="0" step="0.01" />
        </div>
        <div style={{ flex: 1 }}>
          <p>Disc (AED)</p>
          <input type="number" name="discount" value={item.discount} onChange={handleField} min="0" />
        </div>
        <div style={{ flex: 1 }}>
          <p>Tax %</p>
          <input type="number" name="tax" value={item.tax} onChange={handleField} min="0" disabled={taxTreatment === "Non VAT Registered"} />
        </div>
        <div style={{ flex: 1 }}>
          <p>Total</p>
          <input value={(parseFloat(item.total) || 0).toFixed(2)} disabled style={{ background: "#f3f4f6", fontWeight: "600" }} />
        </div>
      </div>
    </div>
  );
}

function CreateInvoice() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [customers, setCustomers] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [products, setProducts] = useState([]);

  // Issue #3 — Quick Add Customer modal state
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [addCustLoading, setAddCustLoading] = useState(false);
  const [quickCustomer, setQuickCustomer] = useState({ name: "", companyName: "", email: "", phone: "", address: "" });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [custRes, soRes, prodRes, nextNumRes] = await Promise.all([
          dataService.getCustomers(),
          dataService.getOrders("Sales Order"),
          dataService.getProducts(),
          dataService.getNextInvoiceNumber()
        ]);
        setCustomers(Array.isArray(custRes.data.data) ? custRes.data.data : []);
        setSalesOrders(Array.isArray(soRes.data.data) ? soRes.data.data : []);
        setProducts(Array.isArray(prodRes.data.data) ? prodRes.data.data : []);
        if (nextNumRes.data.success) {
          setFormData(prev => ({ ...prev, orderId: nextNumRes.data.nextNumber }));
        }
      } catch (err) {
        console.error("Failed to fetch data", err);
      }
    };
    fetchData();
  }, []);

  const [formData, setFormData] = useState({
    orderId: "",
    soNumber: "",
    customer: "",
    date: new Date().toISOString().split("T")[0],
    dueDate: "",
    shippingAddress: "",
    shippingCost: 0,
    shippingMethod: "standard method",
    status: "Outstanding",
    paymentStatus: "UnPaid",
    salesPerson: "john doe",
    customerNotes: "",
    internalNotes: "",
    paymentMethod: "Cash",
    customerEmail: "",
    customerPhone: "",
    taxTreatment: "VAT Registered",
    trn: "",
    placeOfSupply: "Dubai",
    paymentTerms: "Due on Receipt"
  });

  const [items, setItems] = useState([
    { product: "", description: "", quantity: 1, price: 0, discount: 0, tax: 5, total: 0 }
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: name === "shippingCost" ? parseFloat(value) || 0 : value };

      if (name === "customer") {
        const c = customers.find(c => c.name === value);
        if (c) {
          updated.shippingAddress = c.address || "";
          updated.taxTreatment = c.taxTreatment || "VAT Registered";
          updated.trn = c.trn || "";
          updated.placeOfSupply = c.placeOfSupply || "Dubai";
          updated.paymentTerms = c.paymentTerms || "Due on Receipt";
          updated.customerEmail = c.email || "";
          updated.customerPhone = c.phone || "";
        }
        updated.soNumber = "";
      }

      if (name === "soNumber") {
        const so = salesOrders.find(so => so.orderId === value);
        if (so) {
          setItems(so.items.map(item => ({ ...item, description: item.description || "", total: item.total || 0 })));
          updated.shippingAddress = so.shippingAddress || updated.shippingAddress;
        }
      }
      return updated;
    });
  };

  // Issue #9: updated handleItemChange to handle free-text product + description
  const handleItemChange = (index, e) => {
    const { name, value } = e.target;
    const newItems = [...items];

    if (name === "product") {
      newItems[index].product = value;
      const match = products.find(p => p.name === value);
      if (match) {
        newItems[index].price = match.retailPrice || 0;
        newItems[index].tax = formData.taxTreatment === "Non VAT Registered" ? 0 : (match.taxRate || 5);
        const sub = (newItems[index].quantity * newItems[index].price) - (newItems[index].discount || 0);
        newItems[index].total = sub + (sub * newItems[index].tax / 100);
      }
    } else if (name === "description") {
      newItems[index].description = value;
    } else {
      newItems[index][name] = parseFloat(value) || 0;
      if (formData.taxTreatment === "Non VAT Registered") newItems[index].tax = 0;
      const sub = (newItems[index].quantity * newItems[index].price) - (newItems[index].discount || 0);
      newItems[index].total = sub + (sub * (newItems[index].tax || 0) / 100);
    }
    setItems(newItems);
  };

  const addItem = () => setItems([...items, { product: "", description: "", quantity: 1, price: 0, discount: 0, tax: formData.taxTreatment === "Non VAT Registered" ? 0 : 5, total: 0 }]);
  const removeItem = (index) => { if (items.length > 1) setItems(items.filter((_, i) => i !== index)); };

  const calcGrandTotal = () => items.reduce((s, i) => s + (parseFloat(i.total) || 0), 0) + (parseFloat(formData.shippingCost) || 0);
  const calcTotalTax = () => items.reduce((s, i) => {
    const sub = ((parseFloat(i.quantity) || 0) * (parseFloat(i.price) || 0)) - (parseFloat(i.discount) || 0);
    return s + (sub * (parseFloat(i.tax) || 0) / 100);
  }, 0);

  // Issue #3: Quick Add Customer
  const handleQuickAddCustomer = async (e) => {
    e.preventDefault();
    if (!quickCustomer.name) return;
    setAddCustLoading(true);
    try {
      await dataService.addCustomer({ ...quickCustomer, status: "Active", taxTreatment: "VAT Registered" });
      const res = await dataService.getCustomers();
      const updated = Array.isArray(res.data.data) ? res.data.data : [];
      setCustomers(updated);
      setFormData(prev => ({ ...prev, customer: quickCustomer.name, shippingAddress: quickCustomer.address || "" }));
      setQuickCustomer({ name: "", companyName: "", email: "", phone: "", address: "" });
      setShowAddCustomer(false);
    } catch (err) {
      alert("Failed to add customer: " + (err.response?.data?.message || err.message));
    } finally {
      setAddCustLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const payload = {
      ...formData,
      type: "Invoice",
      items: items.map(item => ({
        ...item,
        tax: formData.taxTreatment === "Non VAT Registered" ? 0 : item.tax,
        total: formData.taxTreatment === "Non VAT Registered" ? (item.quantity * item.price) - item.discount : item.total
      })),
      tax: formData.taxTreatment === "Non VAT Registered" ? 0 : calcTotalTax(),
      amount: calcGrandTotal()
    };
    try {
      await dataService.addOrder(payload);
      navigate("/invoices");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create invoice");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-vendor-page">
      <div className="page-header">
        <h1>Create Invoice</h1>
        <p>Generate a new invoice for customers</p>
      </div>

      {error && <div className="error-message" style={{ color: "red", marginBottom: "20px" }}>{error}</div>}

      {/* Issue #3 — Quick Add Customer Modal */}
      <Modal isOpen={showAddCustomer} onClose={() => setShowAddCustomer(false)} title="Quick Add Customer"
        footer={<>
          <button className="secondary-btn" type="button" onClick={() => setShowAddCustomer(false)}>Cancel</button>
          <button className="primary-btn" type="button" onClick={handleQuickAddCustomer} disabled={addCustLoading}>
            {addCustLoading ? "Saving..." : "Add Customer"}
          </button>
        </>}
      >
        <div className="modal-form">
          <div className="form-group"><label>Full Name *</label><input value={quickCustomer.name} onChange={e => setQuickCustomer({ ...quickCustomer, name: e.target.value })} placeholder="Customer name" required /></div>
          <div className="form-group"><label>Company Name</label><input value={quickCustomer.companyName} onChange={e => setQuickCustomer({ ...quickCustomer, companyName: e.target.value })} placeholder="Company (optional)" /></div>
          <div className="form-group"><label>Email</label><input type="email" value={quickCustomer.email} onChange={e => setQuickCustomer({ ...quickCustomer, email: e.target.value })} placeholder="email@example.com" /></div>
          <div className="form-group"><label>Phone</label><input value={quickCustomer.phone} onChange={e => setQuickCustomer({ ...quickCustomer, phone: e.target.value })} placeholder="+971-xxx-xxxxx" /></div>
          <div className="form-group"><label>Address</label><textarea value={quickCustomer.address} onChange={e => setQuickCustomer({ ...quickCustomer, address: e.target.value })} placeholder="Customer address" rows={2} style={{ width: "100%" }} /></div>
        </div>
      </Modal>

      <form onSubmit={handleSubmit}>
        <div className="vendor-grid">
          <div className="vendor-left">
            <div className="vendor-card">
              <h2>Invoice Details</h2>
              <div className="v-name">
                <div>
                  <p>Invoice Number</p>
                  {/* Issue #1 already editable here */}
                  <input name="orderId" value={formData.orderId} onChange={handleChange} required placeholder="INV-xxx" />
                </div>
                <div>
                  <p>SO Number (Ref)</p>
                  <select name="soNumber" value={formData.soNumber} onChange={handleChange}>
                    <option value="">Select Sales Order (Optional)</option>
                    {salesOrders.filter(so => so.customer === formData.customer).map(so => (
                      <option key={so._id} value={so.orderId}>{so.orderId} — {new Date(so.date).toLocaleDateString()}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="v-name">
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <p style={{ margin: 0 }}>Customer</p>
                    {/* Issue #3 — inline add customer */}
                    <button type="button" onClick={() => setShowAddCustomer(true)}
                      style={{ fontSize: "12px", color: "#7C3AED", background: "none", border: "none", cursor: "pointer", fontWeight: "600" }}>
                      + New Customer
                    </button>
                  </div>
                  <select name="customer" value={formData.customer} onChange={handleChange} required style={{ marginTop: "4px" }}>
                    <option value="">Select Customer</option>
                    {customers.map(c => <option key={c._id} value={c.name}>{c.name}{c.companyName ? ` (${c.companyName})` : ""}</option>)}
                  </select>
                </div>
              </div>

              <div className="v-name">
                <div><p>Invoice Date</p><input type="date" name="date" value={formData.date} onChange={handleChange} required /></div>
                <div><p>Due Date</p><input type="date" name="dueDate" value={formData.dueDate} onChange={handleChange} required /></div>
              </div>

              <div className="v-name">
                <div><p>Billing / Shipping Address</p><input name="shippingAddress" value={formData.shippingAddress} onChange={handleChange} placeholder="Enter address" /></div>
              </div>

              <div className="v-name">
                <div>
                  <p>Tax Treatment</p>
                  <select name="taxTreatment" value={formData.taxTreatment} onChange={handleChange}>
                    <option value="VAT Registered">VAT Registered (5%)</option>
                    <option value="Non VAT Registered">Non VAT Registered (0%)</option>
                  </select>
                </div>
                <div>
                  <p>Warehouse</p>
                  <select name="warehouse" value={formData.warehouse || "Main Warehouse - dubai"} onChange={handleChange}>
                    <option value="Main Warehouse - dubai">Main Warehouse - Dubai</option>
                    <option value="West Coast Hub - sharjah">West Coast Hub - Sharjah</option>
                  </select>
                </div>
                {formData.taxTreatment === "VAT Registered" && (
                  <div><p>TRN</p><input name="trn" value={formData.trn || ""} onChange={handleChange} placeholder="Enter TRN" /></div>
                )}
              </div>

              <div className="v-name">
                <div>
                  <p>Place of Supply</p>
                  <select name="placeOfSupply" value={formData.placeOfSupply} onChange={handleChange}>
                    {["Dubai","Abu Dhabi","Sharjah","Ajman","Umm Al Quwain","Ras Al Khaimah","Fujairah","Outside UAE"].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <p>Payment Terms</p>
                  <select name="paymentTerms" value={formData.paymentTerms} onChange={handleChange}>
                    <option value="Due on Receipt">Due on Receipt</option>
                    <option value="Net 15">Net 15</option>
                    <option value="Net 30">Net 30</option>
                    <option value="Net 45">Net 45</option>
                    <option value="Net 60">Net 60</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="vendor-card">
              <h2>Order Items</h2>
              {items.map((item, index) => (
                <ItemRow key={index} item={item} index={index} products={products} taxTreatment={formData.taxTreatment}
                  onChange={handleItemChange} onRemove={removeItem} showRemove={items.length > 1} />
              ))}
              <button type="button" onClick={addItem} className="secondary-btn" style={{ marginTop: "10px" }}>+ Add Item</button>
            </div>

            <div className="vendor-card">
              <h2>Additional Information</h2>
              <div className="v-name">
                <div><p>Shipping Cost</p><input type="number" name="shippingCost" value={formData.shippingCost} onChange={handleChange} /></div>
                <div>
                  <p>Shipping Method</p>
                  <select name="shippingMethod" value={formData.shippingMethod} onChange={handleChange}>
                    <option value="standard method">Standard Method</option>
                    <option value="express method">Express Method</option>
                    <option value="overnight shipping">Overnight Shipping</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="vendor-right">
            <div className="vendor-card">
              <div className="v-namer">
                <h2>Invoice Summary</h2>
                <div>
                  <p>Grand Total</p>
                  <input value={`AED ${calcGrandTotal().toFixed(2)}`} disabled style={{ fontSize: "1.2rem", fontWeight: "bold" }} />
                </div>
              </div>
            </div>

            <div className="vendor-card">
              <div className="v-namer">
                <h2>Invoice Settings</h2>
                <div>
                  <p>Invoice Status</p>
                  <select name="status" value={formData.status} onChange={handleChange}>
                    <option value="Draft">Draft</option>
                    <option value="Outstanding">Outstanding</option>
                    <option value="Paid">Paid</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                  <p>Payment Status</p>
                  <select name="paymentStatus" value={formData.paymentStatus} onChange={handleChange}>
                    <option value="UnPaid">UnPaid</option>
                    <option value="PartiallyPaid">Partially Paid</option>
                    <option value="Paid">Paid</option>
                  </select>
                  {/* Issue #8 — Payment method in create */}
                  <p>Payment Method</p>
                  <select name="paymentMethod" value={formData.paymentMethod} onChange={handleChange}>
                    <option value="Cash">Cash</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Online / Card">Online / Card</option>
                    <option value="Payment Link">Payment Link</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Other">Other</option>
                  </select>
                  <p>Sales Person</p>
                  <select name="salesPerson" value={formData.salesPerson} onChange={handleChange}>
                    <option value="john doe">john doe</option>
                    <option value="jane smith">jane smith</option>
                    <option value="Mike johnson">Mike johnson</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="vendor-card">
              <h2>Notes</h2>
              <p>Customer Notes (visible on invoice)</p>
              <textarea name="customerNotes" value={formData.customerNotes} onChange={handleChange} placeholder="e.g. Thank you for your business!" rows={3} style={{ width: "100%", marginBottom: "10px" }} />
              <p>Internal Notes</p>
              <textarea name="internalNotes" value={formData.internalNotes} onChange={handleChange} placeholder="Not visible to customer" rows={3} style={{ width: "100%" }} />
            </div>

            <div className="vendor-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                <h2>Attachments</h2>
                <label className="secondary-btn" style={{ fontSize: "12px", padding: "4px 8px", cursor: "pointer" }}>
                  + Add File
                  <input type="file" multiple style={{ display: "none" }} onChange={(e) => {
                    const files = Array.from(e.target.files);
                    setFormData(prev => ({ ...prev, attachments: [...(prev.attachments || []), ...files.map(f => ({ name: f.name, size: f.size, type: f.type, lastModified: f.lastModified }))] }));
                  }} />
                </label>
              </div>
              <div className="attachments-list">
                {(formData.attachments || []).length === 0 ? (
                  <p style={{ fontSize: "12px", color: "#9CA3AF", textAlign: "center", padding: "10px", border: "1px dashed #e5e7eb", borderRadius: "6px" }}>
                    No files attached
                  </p>
                ) : (
                  formData.attachments.map((file, idx) => (
                    <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px", background: "#f9fafb", borderRadius: "6px", marginBottom: "6px", border: "1px solid #e5e7eb" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                        <span style={{ fontSize: "16px" }}>📎</span>
                        <div style={{ overflow: "hidden" }}>
                          <p style={{ fontSize: "12px", fontWeight: "600", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</p>
                          <p style={{ fontSize: "10px", color: "#6B7280", margin: 0 }}>{(file.size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                      <button type="button" onClick={() => {
                        setFormData(prev => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== idx) }));
                      }} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: "16px" }}>×</button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="vendor-actions">
              <button type="button" className="secondary-btn" onClick={() => navigate("/invoices")}>Cancel</button>
              <button type="submit" className="primary-btn" disabled={loading}>{loading ? "Creating..." : "Create Invoice"}</button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default CreateInvoice;
